
import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import M from "materialize-css";
import AWS from "aws-sdk";
import "./CreatePost.css";
import { UserContext } from "../../contexts/UserContext";
import API_BASE_URL from "../../config";

const CreatePost = () => {
  const history = useHistory();
  const { state, dispatch } = useContext(UserContext);
  const [description, setDescription] = useState("");
  const [widgetNumber, setWidgetNumber] = useState("");
  const [flag, setFlag] = useState(true);
  const [video, setVideo] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [data, setData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editWidgetNumber, setEditWidgetNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [editTitle, setEditTitle] = useState("");

  // ⬇️ New: genre-related state
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("jwt")) {
      history.push("/signin");
    } else {
      fetch(`${API_BASE_URL}/videos`)
        .then((res) => res.json())
        .then((result) => {
          setData(result.videos);
        });

      // ⬇️ New: Fetch genres
      fetch(`${API_BASE_URL}/genres`)
        .then((res) => res.json())
        .then(setGenres)
        .catch(console.error);
    }
  }, [history]);

  const getPresignedUrl = async (file) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/generate-presigned-url?fileName=${file.name}&fileType=${file.type}`
      );
      const { url } = await response.json();
      return url;
    } catch (err) {
      console.error("Error getting pre-signed URL:", err);
      return null;
    }
  };

  const uploadFile = async (file) => {
    const presignedUrl = await getPresignedUrl(file);
    if (!presignedUrl) {
      M.toast({
        html: "Failed to get upload URL",
        classes: "#c62828 red darken-3",
      });
      return null;
    }

    try {
      const response = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!response.ok) throw new Error("Failed to upload file");
      return presignedUrl.split("?")[0];
    } catch (err) {
      console.error("File upload error:", err);
      M.toast({ html: "File upload failed", classes: "#c62828 red darken-3" });
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const videoUrl = await uploadFile(video);
    const thumbnailUrl = await uploadFile(thumbnail);

    if (!videoUrl || !thumbnailUrl || !selectedGenre) {
      setLoading(false);
      M.toast({
        html: "Please complete all fields",
        classes: "#c62828 red darken-3",
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/create-post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        },
        body: JSON.stringify({
          title,
          description,
          widget_number: widgetNumber,
          flag,
          s3_url: videoUrl,
          thumbnail_url: thumbnailUrl,
          genre_id: selectedGenre, // ✅ Send genre_id
        }),
      });

      const data = await response.json();
      if (response.ok) {
        M.toast({
          html: "Video uploaded successfully",
          classes: "#43a047 green darken-1",
        });

        setTitle("");
        setDescription("");
        setWidgetNumber("");
        setFlag(true);
        setVideo(null);
        setThumbnail(null);
        setSelectedGenre("");

        document
          .querySelectorAll(".file-path, input[type=file]")
          .forEach((input) => {
            input.value = "";
          });

        fetch(`${API_BASE_URL}/videos`)
          .then((res) => res.json())
          .then((result) => {
            setData(result.videos);
          });
      } else {
        M.toast({ html: data.error, classes: "#c62828 red darken-3" });
      }
    } catch (error) {
      console.error("Error creating post:", error);
      M.toast({
        html: "Failed to create post",
        classes: "#c62828 red darken-3",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index, video) => {
    setEditIndex(index);
    setEditTitle(video.title);
    setEditDescription(video.description);
    setEditWidgetNumber(video.widget_number);
  };

  const handleSave = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/update-post/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("jwt"),
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          widget_number: editWidgetNumber,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        M.toast({
          html: "Video updated successfully",
          classes: "#43a047 green darken-1",
        });
        setEditIndex(null);
        fetch(`${API_BASE_URL}/videos`)
          .then((res) => res.json())
          .then((result) => {
            setData(result.videos);
          });
      } else {
        M.toast({ html: data.error, classes: "#c62828 red darken-3" });
      }
    } catch (error) {
      console.error("Error updating post:", error);
      M.toast({
        html: "Failed to update post",
        classes: "#c62828 red darken-3",
      });
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this video? If you press yes, the video will still show on this page but will not show on the Vote page."
      )
    ) {
      try {
        const response = await fetch(`${API_BASE_URL}/flag-post/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify({ flag: false }),
        });

        const data = await response.json();
        if (response.ok) {
          M.toast({
            html: "Video flagged successfully",
            classes: "#43a047 green darken-1",
          });
          fetch(`${API_BASE_URL}/videos`)
            .then((res) => res.json())
            .then((result) => {
              setData(result.videos);
            });
        } else {
          M.toast({ html: data.error, classes: "#c62828 red darken-3" });
        }
      } catch (error) {
        console.error("Error flagging post:", error);
        M.toast({
          html: "Failed to flag post",
          classes: "#c62828 red darken-3",
        });
      }
    }
  };

  return (
    <div className="create-post-container">
      <form className="card input-field" onSubmit={handleSubmit}>
        <h1>Create Post</h1>
        <fieldset>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <input
            type="text"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />

          <input
            type="text"
            placeholder="Widget Number"
            value={widgetNumber}
            onChange={(e) => setWidgetNumber(e.target.value)}
            required
          />

          {/* ⬇️ Genre Dropdown */}
          <div className="input-field">
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              required
            >
              <option value="">Select Genre</option>
              {genres.map((genre) => (
                <option key={genre.id} value={genre.id}>
                  {genre.name}
                </option>
              ))}
            </select>
          </div>

          <div className="file-field input-field">
            <div className="btn">
              <span>Upload Video</span>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setVideo(e.target.files[0])}
                required
              />
            </div>
          </div>

          <div className="file-field input-field">
            <div className="btn">
              <span>Upload Thumbnail</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnail(e.target.files[0])}
                required
              />
            </div>
          </div>
        </fieldset>

        <input type="submit" value="Submit Post" />
        {loading && <div className="loading-spinner">Loading...</div>}
      </form>

      {/* Videos Table */}
      <h2>Uploaded Videos</h2>
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Description</th>
            <th>Widget Number</th>
            <th>Video</th>
            <th>Votes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.id}>
              <td>
                {editIndex === index ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />
                ) : (
                  <strong>{item.title}</strong>
                )}
              </td>
              <td>
                {editIndex === index ? (
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                ) : (
                  item.description
                )}
              </td>
              <td>
                {editIndex === index ? (
                  <input
                    type="text"
                    value={editWidgetNumber}
                    onChange={(e) => setEditWidgetNumber(e.target.value)}
                  />
                ) : (
                  item.widget_number
                )}
              </td>
              <td>
                <video
                  width="150"
                  height="150"
                  controls
                  poster={item.thumbnail_url}
                >
                  <source src={item.s3_url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </td>
              <td>{item.votes}</td>
              <td className="button-group">
                {editIndex === index ? (
                  <button
                    className="edit-button"
                    onClick={() => handleSave(item.id)}
                  >
                    Save
                  </button>
                ) : (
                  <button
                    className="edit-button"
                    onClick={() => handleEdit(index, item)}
                  >
                    Edit
                  </button>
                )}
                <button
                  className="delete-button"
                  onClick={() => handleDelete(item.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CreatePost;
